import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import { Grid } from "@mui/material"
import { MRT_ColumnDef, MRT_Row, MRT_TableInstance } from "material-react-table"
import { useQueryClient } from "@tanstack/react-query"

import {
    getExtraServiceRoute,
    MERCHANT_ADMIN_EXTRA_SERVICES_CREATE_ROUTE,
    MERCHANT_ADMIN_ROUTE,
} from "../../../../../constants/routes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { TableNameLink } from "../../../../../shared/components/table/TableNameLink"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import {
    fetchPriceLists,
    FetchPriceListsResponse,
    NO_PRICE_LIST_ID,
    PriceListQuery,
} from "../../priceList/priceListApi"
import { PriceListDropdownSelector } from "../../priceList/components/PriceListDropdownSelector"
import { EnableProductSort } from "../../priceList/components/EnableProductSort"
import { useMerchant } from "../../../../merchants/merchantHooks"
import {
    ExtraServicesQuery,
    fetchExtraServices,
    FetchExtraServicesResponse,
    sortExtraServiceInPriceList,
} from "../extraServicesApi"
import { getExtraServicePriceSublines, getExtraServiceRowId, reorderExtraServices } from "../extraServicesHelpers"
import { useOptimisticOrder } from "../../products/hooks/useOptimisticOrder"
import { useMatchEnvironment } from "../../../../environment/envHooks"
import { EnvironmentType } from "../../../../environment/envTypes"
import { ExternalServiceEntry } from "../extraServicesTypes"
import {
    defaultConfirmModalState,
    DefaultConfirmModalStateType,
    DeleteExtraServiceDialog,
} from "../components/DeleteExtraServiceDialog"
import { renderRowActionMenuItems, useRowActionMenuItemsStyles } from "../components/renderRowActionMenuItems"
import { useMoney } from "../../../../../modules/currency/useMoney"

const useStyles = makeStyles()(() => ({
    headCell: {
        "& .Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    topToolbar: {
        alignItems: "flex-start",
    },
    // Stack the "Enable product sort" control under the price-list dropdown inside the toolbar.
    toolbarStack: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: ".75rem",
        paddingBottom: ".5rem",
    },
}))

const selectExtraServices = (data: FetchExtraServicesResponse) => data.extraServices

const selectPriceLists = (data: FetchPriceListsResponse) => data?.priceLists ?? []

export function ExtraServices() {
    const { classes } = useStyles()
    const { classes: rowActionClasses } = useRowActionMenuItemsStyles()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { merchantId } = useMerchant()
    const moneyFactory = useMoney({})
    const queryClient = useQueryClient()
    const [searchParams] = useSearchParams()
    const priceListIdParam = searchParams.get("priceListId")

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)
    const [selectedPriceListId, setSelectedPriceListId] = useState<number | typeof NO_PRICE_LIST_ID | null>(
        priceListIdParam ? Number(priceListIdParam) : null,
    )

    const { data: priceLists = [], isLoading: isPriceListsLoading } = useApiQuery(fetchPriceLists, {
        queryName: PriceListQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectPriceLists,
    })

    useEffect(() => {
        if (selectedPriceListId !== null || priceLists.length === 0) return
        const firstActive = priceLists.find((pl) => pl.isActive)
        setSelectedPriceListId(firstActive?.id ?? priceLists[0].id)
    }, [priceLists, selectedPriceListId])

    const services = useApiQuery(fetchExtraServices, {
        queryName: ExtraServicesQuery.LIST,
        dependencies: {
            merchantId,
            priceListId: selectedPriceListId,
        },
        enabled: selectedPriceListId !== null,
        select: selectExtraServices,
    })

    // The sort endpoint is price-list-scoped, so reordering only applies when a concrete
    // price list is selected (not "No price list" or the initial null state).
    const sortPriceListId = typeof selectedPriceListId === "number" ? selectedPriceListId : undefined

    const isResetProductSort =
        priceLists.find((priceList) => priceList.id === sortPriceListId)?.isResetProductSort ?? false

    // Optimistic local ordering so a drag shows instantly; rolls back on failure.
    const { orderedRows, applyOptimisticMove, rollback } = useOptimisticOrder(services.data, reorderExtraServices)

    const { ApiQueryTable, columnFactory } = useApiQueryTable({
        data: orderedRows,
        isInitialLoading: services.isInitialLoading,
        isFetching: services.isFetching,
        isError: services.isError,
    })

    // Sort is applied optimistically on drop. On failure: roll back to the pre-move
    // order, then re-throw so the API error is surfaced as a snackbar.
    const { mutate: callSortExtraService } = useApiMutation(sortExtraServiceInPriceList, {
        async onError(_, error) {
            rollback()
            throw error
        },
    })

    const showSortControls = !useMatchEnvironment([EnvironmentType.QA, EnvironmentType.Prod])
    const allowSort = showSortControls && isResetProductSort

    const enableSortAction =
        showSortControls && !isResetProductSort && sortPriceListId !== undefined ? (
            <EnableProductSort
                priceListId={sortPriceListId}
                onEnabled={() => {
                    queryClient.invalidateQueries({ queryKey: [PriceListQuery.LIST] })
                    queryClient.invalidateQueries({ queryKey: [ExtraServicesQuery.LIST] })
                }}
            />
        ) : null

    const handleDragEnd = useCallback(
        (table: MRT_TableInstance<ExternalServiceEntry>) => {
            const { draggingRow, hoveredRow } = table.getState()
            if (!draggingRow || !hoveredRow) return

            const extraServiceId = draggingRow.original.id
            if (extraServiceId === undefined) return

            const targetRow = hoveredRow as MRT_Row<ExternalServiceEntry>

            // Show the new order immediately (snapshots internally for rollback).
            applyOptimisticMove(draggingRow.original, targetRow.original)

            if (sortPriceListId === undefined) return

            callSortExtraService({
                priceListId: sortPriceListId,
                extraServiceId,
                body: {
                    moveTo: { position: targetRow.index + 1 },
                },
            })
        },
        [sortPriceListId, applyOptimisticMove, callSortExtraService],
    )

    const dragHandleProps = useCallback(
        ({ table }: { table: MRT_TableInstance<ExternalServiceEntry> }) => ({
            onDragEnd: () => handleDragEnd(table),
        }),
        [handleDragEnd],
    )

    const columns = useMemo<MRT_ColumnDef<ExternalServiceEntry>[]>(
        () => [
            columnFactory.nameColumn({
                accessorKey: "name",
                size: COLUMN_SIZE.XL,
                header: l10n.getString("extra-services-services-column"),
                Cell: ({ row }) => {
                    const { id, name } = row.original
                    const { subtitle1, subtitle2, subtitle3 } = getExtraServicePriceSublines(
                        row.original,
                        l10n,
                        moneyFactory,
                    )

                    return (
                        <StandardTwoLinesCell
                            title={
                                id === undefined ? (
                                    name
                                ) : (
                                    <TableNameLink name={name} onClick={() => navigate(getExtraServiceRoute(id))} />
                                )
                            }
                            subtitle1={subtitle1}
                            subtitle2={subtitle2}
                            subtitle3={subtitle3}
                        />
                    )
                },
            }),
            columnFactory.typeColumn({
                id: "serviceType",
                header: l10n.getString("extra-services-service-type-column"),
                size: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
                accessorFn: (row) =>
                    row.serviceType
                        ? l10n.getStringForEnum(
                              "extra-services-create-form-type-dropdown-item",
                              row.serviceType,
                              row.serviceType,
                          )
                        : "",
            }),
            columnFactory.typeColumn({
                id: "pricingType",
                header: l10n.getString("extra-services-price-type-column"),
                size: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
                accessorFn: (row) =>
                    row.pricingType
                        ? l10n.getStringForEnum(
                              "extra-services-create-form-pricing-type-dropdown-item",
                              row.pricingType,
                              row.pricingType,
                          )
                        : "",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    header: l10n.getString("extra-services-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory, moneyFactory, navigate],
    )

    const renderPriceListFilter = useCallback(
        () => (
            <StandardTableFilter variant="left">
                <PriceListDropdownSelector
                    priceLists={priceLists}
                    isLoading={isPriceListsLoading}
                    selectedPriceListId={selectedPriceListId}
                    onPriceListChange={setSelectedPriceListId}
                    showNoPriceListOption
                />
            </StandardTableFilter>
        ),
        [priceLists, isPriceListsLoading, selectedPriceListId],
    )

    return (
        <>
            <LocalizedStrict id="extra-services-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Extra services"
                    page={Page.MERCHANT_EXTRA_SERVICES}
                    rightElement={
                        <LocalizedStrict id="extra-services-create-service-button">
                            <StandardButton
                                onClick={() => {
                                    navigate(MERCHANT_ADMIN_EXTRA_SERVICES_CREATE_ROUTE)
                                }}
                            >
                                Create extra service
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-extra-services">
                            <Breadcrumb>Extra services</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    memoMode="cells"
                    getRowId={getExtraServiceRowId}
                    muiTopToolbarProps={{ className: classes.topToolbar }}
                    renderTopToolbarCustomActions={() => (
                        <div className={classes.toolbarStack}>
                            {renderPriceListFilter()}
                            {enableSortAction}
                        </div>
                    )}
                    enableRowOrdering={allowSort}
                    muiTableBodyRowDragHandleProps={dragHandleProps}
                    initialState={{ density: "spacious" }}
                    muiTableHeadCellProps={{ className: classes.headCell }}
                    renderRowActionMenuItems={({ row, closeMenu }) =>
                        renderRowActionMenuItems({
                            row,
                            closeMenu,
                            setConfirmModalState,
                            navigate,
                            deleteItemClassName: rowActionClasses.deleteItem,
                        })
                    }
                />

                {/* Confirm delete */}
                {confirmModalState.showConfirmModal && (
                    <DeleteExtraServiceDialog
                        confirmModalState={confirmModalState}
                        setConfirmModalState={setConfirmModalState}
                    />
                )}
            </Grid>
        </>
    )
}
